import { trackRtc, type RtcContext } from '../liveTelemetry';
import {
    REMOTE_AUDIBLE_LEVEL,
    REMOTE_AUDIBLE_POLL_MS,
    REMOTE_AUDIBLE_TIMEOUT_MS,
    waitForAudibleLevel,
} from '../../utils/remoteAudioAudible';
import { denoiserStateOf } from './micNoiseSuppressor';

/**
 * CORE-844 — capture-side audio diagnostics.
 *
 * A participant on an external AV device was heard by everyone else with a
 * strongly pitched-up ("chipmunk") voice. Because the *listeners* hear it, the
 * defect is on the publish/encode path, and the classic cause is a sample-rate
 * mismatch: the track is captured at the device's native rate (cheap USB mics
 * are often 16k/32k/44.1k) but resampled/encoded as if it were 48k.
 *
 * None of our publish paths pin a capture rate — Agora's
 * `createMicrophoneAudioTrack()` is called without `encoderConfig`, LiveKit
 * creates the track itself — so today we cannot tell from the server whether a
 * report was a rate mismatch or something else. This module answers exactly
 * that question, and nothing else: it reads the parameters the browser already
 * decided on and ships them through the existing RTC telemetry pipeline.
 *
 * Privacy: device **labels** are never read (they carry the user's hardware
 * name and are PII). `deviceId`/`groupId` are origin-scoped opaque handles,
 * which is what we need to correlate "same device, different session". No
 * audio data of any kind is touched.
 *
 * This is diagnosis, not the fix — pinning `sampleRate: 48000` on publish is
 * step 2, and it is deliberately gated on seeing real numbers first.
 *
 * Attempt 5 (11.08) — the first stand run saw only 48k/mono on 3 built-in 48k
 * devices, which proves nothing about the external-device report. The fields
 * read straight from `getSettings()` on the *sent* track cannot answer it: for
 * Agora the sent track may be a processed WebAudio output, so its channelCount
 * reads a flat 1 and its rate a flat 48k regardless of the real device. So we
 * now also read the RAW device track (`_originMediaStreamTrack`), the requested
 * constraints (`getConstraints()`), and whether a WebAudio graph is interposed
 * (`processorContext`). Each is chosen to CONFIRM OR REFUTE a specific
 * hypothesis on the hardware retest, not to restate what we already know.
 */

/** Event suffix; call sites prefix it with their own surface namespace. */
export const AUDIO_CAPTURE_EVENT = 'audio_capture_params';

/**
 * Unwrap whatever the caller has into a plain `MediaStreamTrack`.
 * Agora local tracks expose `getMediaStreamTrack()`, LiveKit's
 * `LocalAudioTrack` exposes `.mediaStreamTrack`, and `getUserMedia` hands back
 * the real thing directly.
 */
function toMediaStreamTrack(input: any): any | null {
    if (!input) return null;
    try {
        if (typeof input.getSettings === 'function') return input;
        if (typeof input.getMediaStreamTrack === 'function') {
            const mst = input.getMediaStreamTrack();
            return mst && typeof mst.getSettings === 'function' ? mst : null;
        }
        const lk = input.mediaStreamTrack;
        if (lk && typeof lk.getSettings === 'function') return lk;
    } catch {
        // A released/disposed track can throw on access — treat as unreadable.
    }
    return null;
}

/**
 * The rate the page's WebAudio graph runs at. Agora's software AEC/ANS/AGC
 * pipeline resamples through WebAudio, so a device rate that differs from this
 * is the suspected trigger. Probed once per page: constructing a context is
 * cheap and it starts suspended, but doing it per track would be wasteful.
 */
let cachedContextSampleRate: number | null | undefined;
function contextSampleRate(): number | null {
    if (cachedContextSampleRate !== undefined) return cachedContextSampleRate;
    cachedContextSampleRate = null;
    try {
        const Ctor = typeof window !== 'undefined'
            ? ((window as any).AudioContext ?? (window as any).webkitAudioContext)
            : undefined;
        if (Ctor) {
            const ctx = new Ctor();
            const rate = typeof ctx.sampleRate === 'number' ? ctx.sampleRate : null;
            // Free the hardware handle immediately — we only wanted the number.
            try { ctx.close?.(); } catch { /* ignore */ }
            cachedContextSampleRate = rate;
        }
    } catch {
        // No WebAudio (SSR, native, locked-down browser) — the rest still works.
    }
    return cachedContextSampleRate ?? null;
}

/** Test seam: forget the probed context rate. */
export function resetAudioContextProbe(): void {
    cachedContextSampleRate = undefined;
}

/** Read a value behind a getter/property that may throw on a disposed track. */
function safeGet<T>(fn: () => T): T | null {
    try {
        return fn();
    } catch {
        return null;
    }
}

/** getSettings() of any MediaStreamTrack, tolerant of a released track. */
function settingsOf(mst: any): Record<string, any> {
    try {
        return mst?.getSettings?.() ?? {};
    } catch {
        return {};
    }
}

/**
 * A `MediaTrackConstraints` value is either a scalar or a
 * `{ ideal | exact | min | max }` object. Collapse it to the single number/bool
 * we asked for, so it lines up with the scalar `getSettings()` reports.
 */
function constraintValue(v: any): any {
    if (v == null) return null;
    if (typeof v === 'object') return v.exact ?? v.ideal ?? v.min ?? v.max ?? null;
    return v;
}

/**
 * Chrome exposes `default` and `communications` as virtual device slots that
 * stand in for "system default" / "communications default" rather than a
 * concrete device. Requesting one and receiving the underlying concrete
 * `deviceId` is expected routing — not a lost user choice (CORE-856).
 */
function isVirtualDeviceId(id: any): boolean {
    return id === 'default' || id === 'communications';
}

/**
 * Everything we know about how this audio track was captured. Returns `null`
 * when the input is not a readable audio track, so call sites can stay a
 * one-liner without guarding.
 */
export function readAudioCaptureParams(input: any): Record<string, any> | null {
    const mst = toMediaStreamTrack(input);
    if (!mst) return null;

    const settings: any = settingsOf(mst);

    const params: Record<string, any> = {
        sampleRate: settings.sampleRate ?? null,
        sampleSize: settings.sampleSize ?? null,
        channelCount: settings.channelCount ?? null,
        echoCancellation: settings.echoCancellation ?? null,
        noiseSuppression: settings.noiseSuppression ?? null,
        autoGainControl: settings.autoGainControl ?? null,
        latency: settings.latency ?? null,
        // Opaque, origin-scoped handles. NOT `label` — that is the hardware
        // name the user sees and counts as personal data.
        deviceId: settings.deviceId ?? null,
        groupId: settings.groupId ?? null,
        readyState: mst.readyState ?? null,
        trackMuted: typeof mst.muted === 'boolean' ? mst.muted : null,
        trackEnabled: typeof mst.enabled === 'boolean' ? mst.enabled : null,
    };

    const ctxRate = contextSampleRate();
    params.contextSampleRate = ctxRate;
    // The headline signal. Only meaningful when both numbers are known, hence
    // the explicit null rather than a defaulted `false` that would read as
    // "checked and fine".
    params.sampleRateMismatch = typeof params.sampleRate === 'number' && typeof ctxRate === 'number'
        ? params.sampleRate !== ctxRate
        : null;
    params.isNon48k = typeof params.sampleRate === 'number'
        ? params.sampleRate !== 48000
        : null;

    // Whether the device could accept a pinned 48k at all — this is what
    // decides if step 2 risks an OverconstrainedError on real hardware.
    try {
        const caps = mst.getCapabilities?.();
        const capRate = caps?.sampleRate;
        if (capRate) {
            params.capSampleRateMin = capRate.min ?? null;
            params.capSampleRateMax = capRate.max ?? null;
        }
        if (caps?.channelCount) {
            params.capChannelCountMax = caps.channelCount.max ?? null;
        }
    } catch {
        // getCapabilities is not universal (Firefox) — absence is fine.
    }

    // --- CORE-844 attempt 5: fields that DISTINGUISH the hypotheses. -------
    // The stand run saw only 48k/mono because every event was a join on a 48k
    // built-in device, and — more importantly — the fields above read the track
    // we *send*, which for Agora can be a processed WebAudio output rather than
    // the raw device. When the real external device finally runs, these fields
    // must separate "device is 44.1k/stereo and we resampled/downmixed it" (the
    // chipmunk cause) from "device is fine, look elsewhere".

    // CORE-844 attempt 8 — which kind of audio input the browser selected
    // (audioinput, audiooutput, etc.), label-free. Pairs with deviceId to
    // disambiguate default-routing from explicit selection without PII.
    try { params.deviceKind = mst.kind ?? null; } catch { params.deviceKind = null; }

    // (a) What we asked for, next to what we got. `getConstraints()` is the
    //     applied constraint set; a gap versus `getSettings()` means the browser
    //     substituted a device or channel layout under us.
    let constraints: any;
    try { constraints = mst.getConstraints?.() ?? {}; } catch { constraints = {}; }
    params.requestedSampleRate = constraintValue(constraints.sampleRate);
    params.requestedChannelCount = constraintValue(constraints.channelCount);
    params.requestedDeviceId = constraintValue(constraints.deviceId);
    params.requestedEchoCancellation = constraintValue(constraints.echoCancellation);
    params.requestedNoiseSuppression = constraintValue(constraints.noiseSuppression);
    params.requestedAutoGainControl = constraintValue(constraints.autoGainControl);
    // CORE-856: a substitution means the user's *chosen* mic was lost, so the
    // direction matters. Chrome's `default`/`communications` are virtual slots,
    // not concrete devices: asking for one and getting the underlying concrete
    // deviceId back is normal routing, not a lost choice. The original strict
    // `!==` counted that resolution as a substitution — every one of the 17/47
    // "substitutions" measured on dev (11.08–04.09) was this `default → concrete`
    // shape, with zero concrete-id → other-id events. So only a *concrete*
    // requested id that comes back different (including falling back to the
    // `default` sentinel) is a real substitution.
    params.deviceIdSubstituted = params.requestedDeviceId && params.deviceId
        ? (isVirtualDeviceId(params.requestedDeviceId)
            ? false
            : params.requestedDeviceId !== params.deviceId)
        : null;

    // (b) Does the track go through a WebAudio graph — where resampling and
    //     down/upmixing, the two ways a pitch shift is introduced, happen — or
    //     straight to the encoder? Agora keeps the raw capture on
    //     `_originMediaStreamTrack` and, when a processor is piped, routes it
    //     through a processor context before `getMediaStreamTrack()` returns
    //     the processed output.
    //     CORE-844 attempt 11: read the `_processorContext` backing field, NOT
    //     the public `processorContext` getter — in SDK 4.24.2 that getter
    //     lazily BUILDS the processor graph, so the probe used to wire every mic
    //     track into WebAudio itself (and `usesWebAudioGraph` was always true).
    //     The processor context is a wrapper, not an AudioContext: its rate is
    //     behind `getAudioContext()`; `.sampleRate` on it was always undefined,
    //     which is why `graphSampleRate` came back null in all 2231 dev events.
    //     Without a processor, the track source's `context` is Agora's shared
    //     page AudioContext — the one CORE-844 pins to 48 kHz.
    const isAgoraLocal = typeof input?.getMediaStreamTrack === 'function';
    const graphCtx = isAgoraLocal ? safeGet(() => (input as any)._processorContext) : null;
    if (isAgoraLocal) {
        params.usesWebAudioGraph = !!(graphCtx || safeGet(() => (input as any).processor));
        const agoraCtx = safeGet(() => graphCtx?.getAudioContext?.())
            ?? safeGet(() => (input as any)._trackSource?.context);
        // The rate of the AudioContext the audio actually passes through on this
        // client — distinct from `contextSampleRate`, which is only a page probe.
        params.graphSampleRate = typeof agoraCtx?.sampleRate === 'number' ? agoraCtx.sampleRate : null;
    } else {
        // Raw MediaStreamTrack / LiveKit: we cannot introspect a graph, so stay
        // null ("not measured") rather than assert a misleading false.
        params.usesWebAudioGraph = null;
        params.graphSampleRate = null;
    }

    // (c) Device-truth params, read from the RAW capture track, not the sent
    //     one. This is the whole point of attempt 5: a device that delivers
    //     44.1k or stereo shows its real numbers here even when the sent track
    //     was resampled/downmixed to 48k mono — so `channelCount` no longer
    //     reads a flat 1 on hardware and the stereo-as-mono clue survives.
    const origin = safeGet(() => (input as any)?._originMediaStreamTrack);
    const originReadable = !!(origin && typeof origin.getSettings === 'function');
    params.originTrackAvailable = originReadable;
    let deviceSettings: Record<string, any> | null = null;
    if (originReadable) {
        deviceSettings = settingsOf(origin);
        params.deviceSettingsSource = 'origin_track';
    } else if (!isAgoraLocal || params.usesWebAudioGraph === false) {
        // For non-Agora tracks (raw MST / LiveKit) or Agora tracks without
        // a processing graph, the sent track IS the device track — no
        // resampling or downmixing has been applied.
        deviceSettings = settings;
        params.deviceSettingsSource = 'sent_track_fallback';
    } else {
        // Agora track with a processing graph but no readable origin
        // (_originMediaStreamTrack): device truth is lost at this level.
        params.deviceSettingsSource = 'unavailable';
    }
    params.deviceSampleRate = deviceSettings ? (deviceSettings.sampleRate ?? null) : null;
    params.deviceChannelCount = deviceSettings ? (deviceSettings.channelCount ?? null) : null;
    // The device's *capability*, not just what it delivered: a mic that can do
    // stereo but was captured as mono is the classic stereo-read-as-mono setup.
    if (origin) {
        const oc = safeGet(() => origin.getCapabilities?.());
        if (oc?.channelCount) params.deviceCapChannelCountMax = oc.channelCount.max ?? null;
    }

    // Derived clues, each phrased as an explicit null when unmeasurable.
    params.deviceIsNon48k = typeof params.deviceSampleRate === 'number'
        ? params.deviceSampleRate !== 48000
        : null;
    // Sent rate ≠ device rate ⇒ our path resampled ⇒ pitch risk is real.
    params.sentVsDeviceRateMismatch = typeof params.sampleRate === 'number' && typeof params.deviceSampleRate === 'number'
        ? params.sampleRate !== params.deviceSampleRate
        : null;
    // Sent channels ≠ device channels ⇒ a down/upmix happened (stereo→mono is
    // the doubled-frequency "minion" case).
    params.sentVsDeviceChannelMismatch = typeof params.channelCount === 'number' && typeof params.deviceChannelCount === 'number'
        ? params.channelCount !== params.deviceChannelCount
        : null;

    return params;
}

/**
 * The real receive-side sample rate and channel count, read from the codec
 * the browser negotiated for the remote track via `RTCRtpReceiver.getStats()`
 * (`codec` entry → `clockRate` / `channels`).
 *
 * Why this instead of `getSettings()`: `readAudioCaptureParams()` runs on
 * `getMediaStreamTrack().getSettings()`, and a *remote* MediaStreamTrack in
 * Chrome is a synthesized receiving track with no device behind it — its
 * settings are essentially empty, so the receive path kept returning no
 * numbers even after `4d65caf1a` started passing the track itself. The codec
 * clock rate, by contrast, is reported by getStats() on every browser, from
 * the negotiated SDP — no packets need to flow first.
 *
 * Returns `null` when the transceiver/stats are unreachable (pre-subscribe,
 * disposed track, Firefox/Safari quirks). Never throws.
 */
async function readRemoteAudioCodecStats(track: any): Promise<{
    clockRate: number | null;
    channels: number | null;
    mimeType: string | null;
} | null> {
    try {
        const transceiver = track?.getRTCRtpTransceiver?.();
        const receiver = transceiver?.receiver;
        if (!receiver || typeof receiver.getStats !== 'function') return null;
        const report: any = await receiver.getStats();
        if (!report || typeof report.forEach !== 'function') return null;

        // Link inbound-rtp → codecId, then read that exact codec entry.
        let codecId: string | null = null;
        report.forEach((stat: any) => {
            if (stat?.type === 'inbound-rtp' && stat.kind === 'audio' && typeof stat.codecId === 'string') {
                codecId = stat.codecId;
            }
        });

        let codec: any = null;
        report.forEach((stat: any) => {
            if (stat?.type !== 'codec') return;
            if (codecId && stat.id === codecId) { codec = stat; return; }
            // Fallback: the first audio codec when the inbound-rtp link is
            // absent (some engines omit codecId on the receive side).
            if (!codecId && !codec && typeof stat.mimeType === 'string' && stat.mimeType.startsWith('audio/')) {
                codec = stat;
            }
        });

        if (!codec) return null;
        return {
            clockRate: typeof codec.clockRate === 'number' ? codec.clockRate : null,
            channels: typeof codec.channels === 'number' ? codec.channels : null,
            mimeType: typeof codec.mimeType === 'string' ? codec.mimeType : null,
        };
    } catch {
        return null;
    }
}

/**
 * One `remote_audio_received` event per remote speaker per PUBLICATION EPISODE.
 * Before this, the event fired on every incoming track, so a room with N
 * speakers cost every viewer N events — and the ticket author's
 * `device_restored` bursts (5 then 8 track recreations in 3 seconds) multiplied
 * it further, which is why the dedup was introduced (CORE-844 долг).
 *
 * CORE-1298 — but "once per speaker per SESSION" was too coarse, and it cost
 * exactly the measurement this ticket needs. Prod room 210 (05–06.09.2026)
 * collected 107 `remote_audio_received` against 675 `published`: a participant's
 * second, third and tenth unmute produced nothing at all on the receiving side,
 * and «первое включение дало звук не сразу» is a statement about those repeats.
 * The key is now cleared when that speaker leaves the wire
 * ({@link resetRemoteAudioEpisode}, called from the `user-unpublished` handler),
 * so every fresh publication is measured once. The burst the dedup was built for
 * happens INSIDE one episode — no `user-unpublished` in between — and stays
 * suppressed.
 */
const remoteAudioReceivedDedup = new Set<string>();

/** Same discipline for the audible-frame probe — see {@link trackRemoteAudioAudible}. */
const remoteAudioAudibleDedup = new Set<string>();

function episodeKey(rtcSessionId: string, remoteUid: number | string): string {
    return `${rtcSessionId}:${remoteUid}`;
}

/**
 * CORE-1298 — this speaker took their audio off the wire; the next publication
 * from them is a new episode and gets measured again.
 */
export function resetRemoteAudioEpisode(rtcSessionId: string, remoteUid: number | string): void {
    const key = episodeKey(rtcSessionId, remoteUid);
    remoteAudioReceivedDedup.delete(key);
    remoteAudioAudibleDedup.delete(key);
}

/** Test seam — forget which speakers already emitted this session. */
export function resetRemoteAudioReceivedDedup(): void {
    remoteAudioReceivedDedup.clear();
    remoteAudioAudibleDedup.clear();
}

/**
 * Emit the receive-side diagnostics for a remote audio track.
 *
 * The "chipmunk" voice reported in CORE-844 was initially assumed to be a
 * send-side sample-rate mismatch, but the capture telemetry (attempts 5-8)
 * showed every published track at 48k/mono — no path mismatch on capture.
 * The one standout signal is the complainant's `contextSampleRate` of 24000,
 * exactly half of 48k. If the receive-side AudioContext runs at 24k and the
 * played track is 48k, a pitch doubling is a receive-side defect, not a
 * send-side one.
 *
 * The remote track's own rate is read from the negotiated codec via
 * `readRemoteAudioCodecStats()` (`codecClockRate`), because the getSettings()
 * path is empty for synthesized receive tracks. `remoteParamsSource` names
 * which source actually produced the numbers — `rtc_stats`, `get_settings`
 * or `unavailable` — so an empty measurement can't be mistaken for a clean
 * one.
 *
 * Called from `subscribeAndPlay` whenever a remote audio track lands.
 *
 * @param track the remote track wrapper (Agora RemoteAudioTrack / raw MST).
 *              When absent (backward compat), only the context probe is emitted.
 */
export async function trackRemoteAudioReceived(
    eventPrefix: string,
    ctx: RtcContext,
    remoteUid: number,
    track?: any,
    extra?: Record<string, any>,
): Promise<void> {
    try {
        const dedupKey = episodeKey(ctx.rtcSessionId, remoteUid);
        if (remoteAudioReceivedDedup.has(dedupKey)) return;
        remoteAudioReceivedDedup.add(dedupKey);

        const rate = contextSampleRate();
        const trackParams = track ? readAudioCaptureParams(track) : null;
        const codec = track ? await readRemoteAudioCodecStats(track) : null;
        trackRtc(`${eventPrefix}.remote_audio_received`, ctx, {
            remoteUid,
            remoteContextSampleRate: rate,
            remoteParamsSource: codec ? 'rtc_stats' : trackParams ? 'get_settings' : 'unavailable',
            ...(codec
                ? {
                    codecClockRate: codec.clockRate,
                    codecChannels: codec.channels,
                    codecMimeType: codec.mimeType,
                }
                : {}),
            ...(trackParams ?? {}),
            ...(extra ?? {}),
        });
    } catch {
        // Telemetry must never break playback.
    }
}

/**
 * CORE-1298 — the last link of the chain the ticket asks for:
 * `click → local publish/setEnabled → remote user_published → first audible
 * frame`. The first three exist; this is the fourth.
 *
 * `subscribe()` resolving and `play()` being called is NOT sound — between them
 * sit precisely the causes under suspicion here (an empty stream from the SFU, a
 * track pulled back off the wire, a browser refusing autoplay before a gesture).
 * Until somebody measures that gap, «звук не появился» and «звук появился»
 * arrive in the database identical, and the ticket is unresolvable by data.
 *
 * Reads Agora's own per-track volume level (0..1) — no audio content is touched
 * or transmitted, only "how many milliseconds until it stopped being zero".
 * Runs in the background: the caller must NOT await it before playing.
 *
 * Read `audible: false` as «за `timeoutMs` звука не было», not as «сломано»: a
 * person who unmutes and stays quiet looks the same as a dead subscription from
 * here. What separates them is the OTHER end of the chain — the speaker's own
 * `mic_publication_state` for the same `attemptId`, and `peakLevel` (a talking
 * person under the threshold is not the same as a flat zero).
 *
 * Deduped per publication episode, exactly like `remote_audio_received`.
 */
export async function trackRemoteAudioAudible(
    eventPrefix: string,
    ctx: RtcContext,
    remoteUid: number,
    track?: any,
    opts?: { timeoutMs?: number; pollMs?: number; extra?: Record<string, any> },
): Promise<void> {
    try {
        const dedupKey = episodeKey(ctx.rtcSessionId, remoteUid);
        if (remoteAudioAudibleDedup.has(dedupKey)) return;
        remoteAudioAudibleDedup.add(dedupKey);

        const timeoutMs = opts?.timeoutMs ?? REMOTE_AUDIBLE_TIMEOUT_MS;
        const pollMs = opts?.pollMs ?? REMOTE_AUDIBLE_POLL_MS;
        const probe = await waitForAudibleLevel({
            getLevel: () => {
                const level = track?.getVolumeLevel?.();
                return typeof level === 'number' ? level : null;
            },
            now: () => Date.now(),
            sleep: (ms) => new Promise<void>(resolve => { setTimeout(resolve, ms); }),
            timeoutMs,
            pollMs,
        });

        trackRtc(`${eventPrefix}.remote_audio_audible`, ctx, {
            remoteUid,
            audible: probe.audible,
            waitedMs: probe.waitedMs,
            peakLevel: probe.peakLevel,
            polls: probe.polls,
            // The limit this verdict was taken against — a `false` means
            // "silent for THIS long", not "silent forever".
            timeoutMs,
            thresholdLevel: REMOTE_AUDIBLE_LEVEL,
            ...(opts?.extra ?? {}),
        });
    } catch {
        // A diagnostic must never be able to break playback.
    }
}

/**
 * Emit the capture parameters for a freshly created/switched audio track.
 * Never throws: a diagnostic must not be able to break a live broadcast.
 *
 * @param eventPrefix surface namespace, e.g. `room_rtc` / `live_rtc_cohost`
 * @param props       call-site context — provider, which code path, etc.
 */
export function trackAudioCapture(
    eventPrefix: string,
    ctx: RtcContext,
    track: any,
    props?: Record<string, any>,
): void {
    try {
        const params = readAudioCaptureParams(track);
        trackRtc(`${eventPrefix}.${AUDIO_CAPTURE_EVENT}`, ctx, {
            ...(props ?? {}),
            ...(params ?? { unreadable: true }),
            // CORE-1598 — whether RNNoise sits in this mic's path.
            denoiser: denoiserStateOf(track),
        });
    } catch {
        // Telemetry is never worth a crash mid-session.
    }
}
